<template>
  <table class="info-box" data-cy="surface-info-box">
    <thead>
      <tr>
        <th data-cy="surface-power-header">
          SURFACE POWER REQUIRED ⚡ @{{
            format(scenarioCalculations.adjustedPumpHzFrequency.value)
          }}hz
        </th>
        <th data-cy="surface-power-value">
          {{
            format(
              convert(
                scenarioCalculations.surfaceDriveCalculations.requiredKVA.value,
                ApparentPowerUnits.KiloVoltAmps,
                targetApparentPowerUnit
              )
            )
          }}
          {{ targetApparentPowerUnit?.shortLabel }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-if="scenario.equipment?.r_step_down_transformer"
        data-cy="step-down-transformer-row"
      >
        <td>
          <strong>Step Down Transformer</strong><br />
          {{
            computeDownTransLabel(scenario.equipment?.r_step_down_transformer)
          }}
        </td>
        <td>
          <br />
          <template v-if="scenario.equipment?.r_step_down_transformer?.kva">
            {{
              wholeNumberFormat(
                Ratio.toStandardUnit(
                  scenarioCalculations.surfaceDriveCalculations
                    .stepDownTransformerLoadRatio.value
                )
              )
            }}% load
          </template>
        </td>
      </tr>
      <tr
        v-if="scenario.equipment?.use_vsd && scenario.equipment?.r_vsd"
        data-cy="vsd-row"
      >
        <td>
          <strong>VSD</strong><br />{{
            computeVsdLabel(scenario.equipment?.r_vsd)
          }}
        </td>
        <td>
          <br />
          <template v-if="scenario.equipment?.r_vsd?.kva">
            {{
              wholeNumberFormat(
                Ratio.toStandardUnit(
                  scenarioCalculations.surfaceDriveCalculations.vsdLoadRatio
                    .value
                )
              )
            }}% load
          </template>
        </td>
      </tr>
      <tr
        v-if="scenario.equipment?.r_step_up_transformer"
        data-cy="step-up-transformer-row"
      >
        <td>
          <strong>Step Up Transformer</strong>
          <br />
          {{ computeUpTransLabel(scenario.equipment?.r_step_up_transformer) }}
        </td>
        <td>
          <br />
          <template v-if="scenario.equipment?.r_step_up_transformer?.kva">
            {{
              wholeNumberFormat(
                Ratio.toStandardUnit(
                  scenarioCalculations.surfaceDriveCalculations
                    .stepUpTransformerLoadRatio.value
                ) 
              )
            }}% load
          </template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import type { Scenario } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { computeUpTransLabel } from "@/models/equipment/step_up_transformer/properties";
import { computeDownTransLabel } from "@/models/equipment/step_down_transformer/properties";
import { computeVsdLabel } from "@/models/equipment/vsd/properties";
import { convert } from "@/libs/units";
import { Ratio } from "@/libs/units/percentage";
import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import { toRefs } from "vue";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { useSettingsStore } from "@/store";
import { sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  scenario: Scenario;
  scenarioCalculations: ScenarioCalculations;
  sagdMode?: boolean;
}>();
const { userSettingsNumberFormat } = useSettingsStore();

const wholeNumberFormat = Intl.NumberFormat(userSettingsNumberFormat.value, {
  maximumFractionDigits: 0
}).format;
const format = Intl.NumberFormat(userSettingsNumberFormat.value, {
  maximumFractionDigits: 2
}).format;

const { scenario } = toRefs(props);

const sizing = injectStrict(sizingKey, null);
const { getApparentPowerUnit } = useScenarioUnits(scenario, sizing);
const targetApparentPowerUnit = getApparentPowerUnit(
  "Required kVA.convert.kVA"
);
</script>
